/**
 * Whether the workspace's Save control is unavailable, and why.
 *
 * One definition because there were two, and they disagreed. Every host that
 * draws Save — the shared title bar and the Agentic Logic rule page, in both
 * the cluster and the split presentation — computes it here.
 *
 * `isDirty` is deliberately NOT a term. In the split presentation Save is the
 * only verb that persists AND deploys, so gating it on pending EDITS grounds
 * every rule whose draft is saved but not live: a rule whose last publish
 * failed, a rule whose recommendation settings changed
 * (those autosave onto the rule row and take effect at the next publish, so a
 * settings edit leaves the buffer clean and the storefront stale). Each of
 * those is exactly when the merchant needs to publish and the button they are
 * looking at is grey. Re-saving an unchanged draft is a well-defined,
 * idempotent republish; refusing to offer it is not.
 */
export interface WorkspaceSaveAvailabilityInput {
  /** The viewer cannot write this entity at all (e.g. a rule owned elsewhere). */
  isReadOnly: boolean;
  isSavingDraft: boolean;
  isPublishing: boolean;
  /** A buffer that does not parse cannot be persisted, let alone deployed. */
  isSyntaxValid: boolean;
  /**
   * The verdict for the current rule source has not landed yet — the worker
   * is still checking, or the entry moved under an open test tab. Save waits
   * for it, and the hint says so rather than blaming errors that may not be.
   */
  isSyntaxPending: boolean;
}

export type WorkspaceSaveBlockReason =
  | "read_only"
  | "saving"
  | "publishing"
  | "syntax_checking"
  | "syntax_invalid";

/**
 * The single reason Save is blocked, in the order the UI reports it (most
 * actionable last, as with `PublishBlockReason`), or `null` when it is
 * available.
 */
export const workspaceSaveBlockReason = ({
  isReadOnly,
  isSavingDraft,
  isPublishing,
  isSyntaxValid,
  isSyntaxPending,
}: WorkspaceSaveAvailabilityInput): WorkspaceSaveBlockReason | null => {
  if (isReadOnly) {
    return "read_only";
  }
  if (isSavingDraft) {
    return "saving";
  }
  if (isPublishing) {
    return "publishing";
  }
  if (isSyntaxPending) {
    return "syntax_checking";
  }
  if (!isSyntaxValid) {
    return "syntax_invalid";
  }
  return null;
};

/** `true` exactly when {@link workspaceSaveBlockReason} names a reason. */
export const isWorkspaceSaveDisabled = (
  input: WorkspaceSaveAvailabilityInput
): boolean => workspaceSaveBlockReason(input) !== null;
